import type { Config } from "@wagmi/core"
import type { Address } from "viem"
import { runInAction } from "mobx"
import { isAddressEqual, zeroAddress } from "viem"

import type { IEventBus } from "@/domains/events"
import type { OrchestratedQueryStore } from "@/domains/library"
import type { IOrchestratedQuerySender } from "@/domains/library/orchestrated-query-interfaces"
import type { WalletConnectionStore } from "@/domains/wallet"
import { LcAddress } from "@/domains/core"
import { OrchestratedQuery } from "@/domains/library"
import { superVoterUserManagerOf } from "@/domains/on-chain"
import { WalletConnectedEvent, WalletDisconnectedEvent } from "@/domains/wallet/events"

import type { ManagerStore } from "./manager-store"

export class ManagerApprovalOrchestrator {
  private approvalStatusQuery: OrchestratedQuery<Map<LcAddress, boolean>> | undefined
  private walletConnectedHandler: ((event: WalletConnectedEvent) => void) | undefined
  private walletDisconnectedHandler: ((event: WalletDisconnectedEvent) => void) | undefined

  constructor(
    private readonly managerStore: ManagerStore,
    private readonly walletStore: WalletConnectionStore,
    private readonly config: Config,
    private readonly chainId: number,
    private readonly superVoterAddress: Address,
    private readonly orchestratedQueryStore: OrchestratedQueryStore,
    private readonly eventBus: IEventBus,
  ) {
    // Subscribe to wallet events
    this.walletConnectedHandler = this.handleWalletConnect.bind(this)
    this.walletDisconnectedHandler = this.handleWalletDisconnect.bind(this)
    this.eventBus.subscribe(WalletConnectedEvent.TYPE, this.walletConnectedHandler)
    this.eventBus.subscribe(WalletDisconnectedEvent.TYPE, this.walletDisconnectedHandler)
  }

  initialize(): void {
    // Create query to poll approval status for all manager addresses
    this.approvalStatusQuery = new OrchestratedQuery<Map<LcAddress, boolean>>(
      "Manager Approval Status Sync",
      (_query) => this.onLoadApprovalStatusTrigger(), // Query parameter available but not used (no chunking)
      60_000, // Poll every minute
      this.onLoadApprovalStatusSuccess.bind(this),
      this.onLoadApprovalStatusFailed.bind(this),
      this.orchestratedQueryStore,
    )

    // Start polling if wallet is connected
    if (this.walletStore.isConnected) {
      this.approvalStatusQuery.start()
    }
  }

  private handleWalletConnect(_event: WalletConnectedEvent): void {
    // Start polling when wallet connects
    if (this.approvalStatusQuery) {
      this.approvalStatusQuery.start()
    }
  }

  private handleWalletDisconnect(_event: WalletDisconnectedEvent): void {
    // Stop polling when wallet disconnects
    if (this.approvalStatusQuery) {
      this.approvalStatusQuery.stop()
    }
    // Clear approval status
    runInAction(() => {
      for (const address of this.managerStore.managerAddresses) {
        this.managerStore.setManagerApprovalStatus(address, false)
      }
    })
  }

  private async onLoadApprovalStatusTrigger(): Promise<Map<LcAddress, boolean>> {
    const connectedWalletAddress = this.walletStore.connectedAddress?.toViemAddress()
    if (!connectedWalletAddress || isAddressEqual(connectedWalletAddress, zeroAddress)) {
      return new Map()
    }

    const approvalStatus = new Map<LcAddress, boolean>()

    // Check approval status for each manager address
    for (const managerAddress of this.managerStore.managerAddresses) {
      try {
        const delegatedManager = await superVoterUserManagerOf({
          account: managerAddress.toViemAddress(),
          chainId: this.chainId,
          config: this.config,
          superVoterAddress: this.superVoterAddress,
        })

        // `undefined` means the read failed (RPC blip, contract missing on
        // this chain, etc.). Treat it as "not approved" rather than letting
        // `isAddressEqual` crash on a non-address input.
        const isApproved =
          delegatedManager !== undefined &&
          isAddressEqual(delegatedManager, connectedWalletAddress)
        approvalStatus.set(managerAddress, isApproved)
      } catch (error) {
        console.error(`Failed to check approval status for ${managerAddress.toString()}:`, error)
        approvalStatus.set(managerAddress, false)
      }
    }

    return approvalStatus
  }

  private onLoadApprovalStatusSuccess(
    _sender: IOrchestratedQuerySender<Map<LcAddress, boolean>>,
    data: Map<LcAddress, boolean>,
  ): void {
    try {
      runInAction(() => {
        // Update approval status for each manager address
        for (const [address, isApproved] of data.entries()) {
          this.managerStore.setManagerApprovalStatus(address, isApproved)
        }
      })
    } catch (error) {
      console.error("Error processing manager approval status:", error)
    }
  }

  private onLoadApprovalStatusFailed(_sender: IOrchestratedQuerySender<Map<LcAddress, boolean>>, error: Error): void {
    console.error("Failed to load manager approval status", error)
  }

  get query(): OrchestratedQuery<Map<LcAddress, boolean>> | undefined {
    return this.approvalStatusQuery
  }

  dispose(): void {
    // Unsubscribe from events
    if (this.walletConnectedHandler) {
      this.eventBus.unsubscribe(WalletConnectedEvent.TYPE, this.walletConnectedHandler)
    }
    if (this.walletDisconnectedHandler) {
      this.eventBus.unsubscribe(WalletDisconnectedEvent.TYPE, this.walletDisconnectedHandler)
    }

    if (this.approvalStatusQuery) {
      this.approvalStatusQuery.stop()
      this.approvalStatusQuery.dispose()
    }
  }
}
